import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaShieldAlt,
  FaLock,
  FaFileContract,
  FaUserCheck,
  FaEnvelope,
  FaArrowLeft,
  FaEdit,
  FaCheckCircle,
  FaInfoCircle,
  FaBuilding,
  FaKey,
  FaRegCopy,
  FaSearch,
  FaPrint,
  FaGavel,
  FaDatabase,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";
import BASE_URL from "../utils/Urls";
import KeypadModal from "../models/KeypadModal";

const DEFAULT_POLICY_SECTIONS = [
  {
    id: "sec-1",
    num: 1,
    title: "Information We May Collect",
    icon: FaDatabase,
    category: "data",
    tag: "Data Collection",
    summary: "Account information, work-related info, location, device telemetry, payments, and uploaded documents.",
    content: `Depending on the features you use, we may collect:

1.1 Account Information
• Name;
• Mobile number;
• Email address, where applicable;
• Profile photograph;
• Date of birth or age information, where required;
• Gender or other profile information where voluntarily provided;
• Account credentials and authentication information.
• Daily work selfies.

1.2 Work-Related Information
We may collect information relating to:
• Sector selected;
• Work preferences;
• Skills;
• Experience;
• Availability;
• Work requirements;
• Work location;
• Booking details;
• Requests and responses;
• User-provided descriptions;
• Other information necessary to connect users.

1.3 Location Information
Where you grant permission, we may collect or process location information to:
• Display your approximate/current location;
• Help users discover relevant work opportunities;
• Show nearby users or work opportunities;
• Provide navigation or directions;
• Calculate distance;
• Assist with booking-related functionality;
• Improve Platform functionality and security.
Location accuracy is not guaranteed. Users should independently verify locations before travelling or accepting work.

1.4 Device and Technical Information
We may collect technical information such as:
• Device type;
• Operating system;
• App version;
• IP address;
• Device identifiers where permitted;
• Network information;
• Crash information;
• Log information;
• Security-related information.

1.5 Payment Information
Payments may be processed through third-party payment providers.
Where a third-party payment provider processes card, bank, UPI, or other payment credentials directly, the Company may not receive or store the complete payment credentials.
We may receive information such as:
• Transaction ID;
• Payment status;
• Amount;
• Date/time;
• Payment method;
• Refund status;
• Purchase history.
The exact information received depends on the payment provider and integration used by the Platform.

1.6 Communications
We may collect messages, support requests, complaints, booking-related communications, and other information that users voluntarily submit through the Platform.

1.7 Camera / Gallery / Uploaded Content
If the Platform provides features requiring photographs or uploaded documents, we may request access to the relevant device functionality.
We will use such information only for the purposes disclosed to the user, such as:
• Profile creation;
• Identity or account verification;
• Work-related information;
• Uploading documents;
• Platform functionality;
• Security and fraud prevention.
We will not use uploaded content for unrelated purposes without appropriate authorization or legal basis.`,
  },
  {
    id: "sec-2",
    num: 2,
    title: "How We Use Information",
    icon: FaUserCheck,
    category: "usage",
    tag: "Data Processing",
    summary: "Managing accounts, connecting users, processing bookings & payouts, security, and dispute resolution.",
    content: `We may use information to:
• Create and manage accounts;
• Provide Platform services;
• Connect Work Givers and Work Doers;
• Display relevant work opportunities;
• Process bookings;
• Process payments and refunds;
• Manage holding amounts;
• Prevent fraud and misuse;
• Verify accounts;
• Provide customer support;
• Provide navigation and location functionality;
• Send service-related notifications;
• Maintain security;
• Investigate disputes;
• Comply with legal obligations;
• Improve the Platform;
• Maintain records;
• Enforce our Terms & Conditions.`,
  },
  {
    id: "sec-3",
    num: 3,
    title: "Sharing Information",
    icon: FaFileContract,
    category: "sharing",
    tag: "Information Sharing",
    summary: "Sharing necessary booking info with other users, trusted service providers, and legal authorities.",
    content: `We may share relevant information with:

3.1 Other Users
Information necessary to facilitate a booking or connection may be shown to another user.
For example, depending on the feature, this may include:
• Name;
• Profile photograph;
• Relevant work information;
• Approximate location;
• Booking information;
• Contact information where the Platform permits it.
We will not disclose information that is not necessary for the relevant Platform functionality except where permitted or required by law.

3.2 Service Providers
We may use third-party providers for:
• Cloud hosting;
• Payment processing;
• Maps/navigation;
• SMS/OTP;
• Push notifications;
• Analytics;
• Security;
• Customer support;
• Technical services.
Such providers may process information on our behalf.

3.3 Legal and Regulatory Authorities
We may disclose information where required by applicable law, court order, governmental authority, law enforcement request, fraud investigation, or to protect legal rights and safety.

3.4 Business Transfers
If the Company undergoes a merger, acquisition, restructuring, sale, or transfer of business/assets, information may be transferred as part of that transaction, subject to applicable law.`,
  },
  {
    id: "sec-4",
    num: 4,
    title: "Location Information & Permissions",
    icon: FaShieldAlt,
    category: "location",
    tag: "Location Access",
    summary: "Location permissions policy, map functionality, and user control over device settings.",
    content: `Location access will be requested only where necessary for Platform functionality.
Users may control location permissions through their device settings, subject to the effect that disabling location access may have on Platform features.
The Platform does not guarantee that GPS, map, or location information is accurate.`,
  },
  {
    id: "sec-5",
    num: 5,
    title: "Data Security & Protection",
    icon: FaLock,
    category: "security",
    tag: "Data Security",
    summary: "Technical and organizational measures to safeguard user data against unauthorized access or destruction.",
    content: `We take reasonable technical and organizational measures to protect personal information against unauthorized access, alteration, disclosure, misuse, or destruction.
However, no electronic system or internet transmission can be guaranteed to be completely secure.
Users are responsible for maintaining the confidentiality of their login credentials and must immediately notify us of suspected unauthorized access.`,
  },
  {
    id: "sec-6",
    num: 6,
    title: "Data Retention",
    icon: FaDatabase,
    category: "retention",
    tag: "Data Lifecycle",
    summary: "Retention schedules aligned with service provision, legal compliance, and dispute resolution.",
    content: `We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including:
• Providing the Platform;
• Maintaining transaction records;
• Resolving disputes;
• Preventing fraud;
• Meeting legal and regulatory obligations;
• Enforcing agreements;
• Protecting our legal rights.
After the applicable retention period, information may be deleted, anonymized, or securely disposed of, subject to legal requirements.`,
  },
  {
    id: "sec-7",
    num: 7,
    title: "Account Deletion",
    icon: FaGavel,
    category: "rights",
    tag: "Account Erasure",
    summary: "Procedure to request complete account deletion and associated personal information removal.",
    content: `Users may request deletion of their account through the account deletion/deactivation facility provided in the Platform or by contacting:
Email: support@adda-farm.com

Where applicable, account deletion will result in deletion of associated personal information, subject to information that we are legally required or legitimately permitted to retain, such as transaction records, fraud-prevention records, dispute records, or information required for legal compliance.`,
  },
  {
    id: "sec-8",
    num: 8,
    title: "User Rights and Choices",
    icon: FaUserCheck,
    category: "rights",
    tag: "Statutory Rights",
    summary: "Access, correction, updating, consent withdrawal, and data deletion rights.",
    content: `Subject to applicable law, users may have rights relating to their personal information, including rights concerning:
• Access;
• Correction;
• Updating information;
• Withdrawal of consent where applicable;
• Deletion;
• Complaints or grievances.

Requests may be sent to: support@adda-farm.com`,
  },
  {
    id: "sec-9",
    num: 9,
    title: "Children's Privacy",
    icon: FaShieldAlt,
    category: "legal",
    tag: "Age Eligibility",
    summary: "Protection of minors and age compliance requirements.",
    content: `The Platform is not intended for children below the minimum age permitted under applicable law.
We do not knowingly collect personal information from children in violation of applicable law.
If we become aware that information has been collected improperly from a child, we may take appropriate steps to delete it.`,
  },
  {
    id: "sec-10",
    num: 10,
    title: "Cookies and Similar Technologies",
    icon: FaDatabase,
    category: "technical",
    tag: "Cookies & SDKs",
    summary: "Use of cookies, SDKs, analytics logs, and telemetry for service performance and security.",
    content: `Our website and Platform may use cookies, SDKs, analytics tools, logs, or similar technologies to maintain functionality, security, analytics, and service performance.
Where required, appropriate consent or controls will be provided.`,
  },
  {
    id: "sec-11",
    num: 11,
    title: "Third-Party Services & Integrations",
    icon: FaBuilding,
    category: "integrations",
    tag: "Third Parties",
    summary: "Interactions with third-party payment gateways, mapping tools, and cloud providers.",
    content: `The Platform may contain integrations with third-party services such as payment providers, mapping services, cloud services, analytics providers, communication services, and other technical providers.
Their handling of information may also be governed by their own privacy policies.`,
  },
  {
    id: "sec-12",
    num: 12,
    title: "Changes to This Privacy Policy",
    icon: FaEdit,
    category: "legal",
    tag: "Policy Updates",
    summary: "How updates and revisions to this Privacy Policy are published and communicated.",
    content: `We may update this Privacy Policy when our services, technology, legal requirements, or data practices change.
The updated version will be made available through the Platform or website.`,
  },
  {
    id: "sec-13",
    num: 13,
    title: "Contact and Grievances",
    icon: FaEnvelope,
    category: "contact",
    tag: "Grievance Desk",
    summary: "Official contact details of the Grievance Officer and compliance headquarters.",
    content: `For privacy questions, complaints, or requests:
• Company: ADDA FARM
• Privacy Contact: Grievance Officer
• Email: support@adda-farm.com
• Phone: +91 Support Desk
• Address: Adda Farm Operations Center, India`,
  },
  {
    id: "sec-14",
    num: 14,
    title: "Acceptance & Acknowledgment",
    icon: FaCheckCircle,
    category: "legal",
    tag: "Binding Terms",
    summary: "Confirmation and acceptance of this Privacy Policy upon using Adda Farm services.",
    content: `By using the Platform, you acknowledge that you have read and understood this Privacy Policy.
• App Name: Adda Farm
• Company: ADDA FARM
• Website: https://adda-farm.com`,
  },
];

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState({});

  const [editingTerm, setEditingTerm] = useState(null);
  const [newContent, setNewContent] = useState("");
  const [saving, setSaving] = useState(false);

  const [showKeypad, setShowKeypad] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        // Try dedicated privacy endpoint first
        let res;
        try {
          res = await axios.get(`${BASE_URL}/admin/privacy`);
        } catch (e) {
          res = await axios.get(`${BASE_URL}/admin/terms`);
        }

        if (res.data?.result && res.data.result.length > 0) {
          // If result contains privacy policy, use it
          const firstRow = res.data.result[0];
          if (firstRow.content && (firstRow.content.includes("Privacy Policy") || firstRow.content.includes("Information We May Collect"))) {
            setTerms([firstRow]);
          } else {
            setTerms([{ id: 1, content: buildFullFallbackText(), updated_at: new Date().toISOString() }]);
          }
        } else {
          setTerms([{ id: 1, content: buildFullFallbackText(), updated_at: new Date().toISOString() }]);
        }
        setError(null);
      } catch (err) {
        console.warn("Could not fetch server privacy terms, using fallback:", err.message);
        setTerms([{ id: 1, content: buildFullFallbackText(), updated_at: new Date().toISOString() }]);
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  function buildFullFallbackText() {
    const preamble = "PRIVACY POLICY\nThis Privacy Policy explains how ADDA FARM, operating ADDA FARM, collects, uses, stores, shares, and protects information when you use our mobile application, website, and related services.\nBy using the Platform, you acknowledge this Privacy Policy.\n\n";
    const clauses = DEFAULT_POLICY_SECTIONS.map((sec) => `${sec.num}. ${sec.title}\n${sec.content}`).join("\n\n");
    return preamble + clauses;
  }

  // Advanced Parser for any policy text format
  const parsedPolicyData = useMemo(() => {
    if (!terms || terms.length === 0 || !terms[0]?.content) {
      return { preamble: "", sections: DEFAULT_POLICY_SECTIONS };
    }
    const rawText = terms[0].content;

    let preamble = "";
    let bodyText = rawText;
    const firstClauseIdx = rawText.search(/(?:^|\n)\d+\.\s+/);
    if (firstClauseIdx > 0) {
      preamble = rawText.slice(0, firstClauseIdx).trim();
      bodyText = rawText.slice(firstClauseIdx).trim();
    }

    const splitRegex = /(?=\n\d+\.\s+|^1\.\s+)/g;
    const chunks = bodyText.split(splitRegex).map((c) => c.trim()).filter(Boolean);

    if (chunks.length === 0) {
      return { preamble, sections: DEFAULT_POLICY_SECTIONS };
    }

    const sections = chunks.map((chunk, idx) => {
      const match = chunk.match(/^(\d+)\.\s*([^\n]+)\n?([\s\S]*)$/);
      const num = match ? parseInt(match[1], 10) || idx + 1 : idx + 1;
      const title = match ? match[2].trim() : `Clause ${idx + 1}`;
      const rawContent = match ? match[3].trim() : chunk;

      const defaultMatch = DEFAULT_POLICY_SECTIONS.find((s) => s.num === num);

      // Split into sub-blocks (e.g., 1.1 Account Information, 1.2 Work-Related Information)
      const subParts = rawContent.split(/(?=\n\d+\.\d+\s+)/g);
      const subBlocks = subParts.map((part, pIdx) => {
        const pTrimmed = part.trim();
        const subMatch = pTrimmed.match(/^(\d+\.\d+)\s+([^\n]+)\n?([\s\S]*)$/);
        if (subMatch) {
          const subNum = subMatch[1];
          const subTitle = subMatch[2].trim();
          const subContent = subMatch[3].trim();

          // Parse bullets vs intro text
          const lines = subContent.split("\n").map((l) => l.trim()).filter(Boolean);
          const bullets = [];
          const introLines = [];

          lines.forEach((line) => {
            if (line.startsWith("•") || line.startsWith("-") || line.startsWith("*")) {
              bullets.push(line.replace(/^[•\-*]\s*/, "").replace(/;$/, ""));
            } else {
              introLines.push(line);
            }
          });

          return {
            id: `sub-${num}-${pIdx}`,
            subNum,
            subTitle,
            intro: introLines.join(" "),
            bullets,
            raw: subContent,
          };
        } else {
          // Standard block
          const lines = pTrimmed.split("\n").map((l) => l.trim()).filter(Boolean);
          const bullets = [];
          const introLines = [];

          lines.forEach((line) => {
            if (line.startsWith("•") || line.startsWith("-") || line.startsWith("*")) {
              bullets.push(line.replace(/^[•\-*]\s*/, "").replace(/;$/, ""));
            } else {
              introLines.push(line);
            }
          });

          return {
            id: `sub-${num}-${pIdx}`,
            subNum: null,
            subTitle: null,
            intro: introLines.join("\n"),
            bullets,
            raw: pTrimmed,
          };
        }
      });

      return {
        id: `sec-${num}`,
        num,
        title: title.charAt(0).toUpperCase() + title.slice(1),
        icon: defaultMatch?.icon || FaFileContract,
        category: defaultMatch?.category || "general",
        tag: defaultMatch?.tag || `Clause ${num}`,
        summary: defaultMatch?.summary || rawContent.slice(0, 110) + "...",
        rawContent,
        subBlocks,
      };
    });

    return { preamble, sections };
  }, [terms]);

  // Filter sections by search query and category
  const filteredSections = useMemo(() => {
    return parsedPolicyData.sections.filter((sec) => {
      const matchesCat =
        activeCategory === "all" ||
        sec.category === activeCategory ||
        (activeCategory === "security" && sec.category === "data");
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        sec.title.toLowerCase().includes(q) ||
        sec.rawContent.toLowerCase().includes(q) ||
        sec.tag.toLowerCase().includes(q);
      return matchesCat && matchesSearch;
    });
  }, [parsedPolicyData, activeCategory, searchQuery]);

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const expandAll = () => {
    const all = {};
    parsedPolicyData.sections.forEach((s) => {
      all[s.id] = true;
    });
    setExpandedSections(all);
  };

  const collapseAll = () => {
    setExpandedSections({});
  };

  const startEdit = (term) => {
    setEditingTerm(term);
    setNewContent(term.content);
    setUnlocked(false);
  };

  const unlockEdit = () => {
    setUnlocked(true);
    setShowKeypad(false);
  };

  const handleSave = async () => {
    if (!editingTerm || !unlocked) return;
    setSaving(true);
    try {
      if (editingTerm.id) {
        await axios.put(`${BASE_URL}/admin/terms/${editingTerm.id}`, {
          content: newContent,
        });
      }

      setTerms((prev) =>
        prev.map((t) =>
          t.id === editingTerm.id
            ? { ...t, content: newContent, updated_at: new Date().toISOString() }
            : t
        )
      );

      setEditingTerm(null);
      setNewContent("");
    } catch (err) {
      alert(err.response?.data?.message || "Saved locally");
      setTerms((prev) =>
        prev.map((t) =>
          t.id === editingTerm.id
            ? { ...t, content: newContent, updated_at: new Date().toISOString() }
            : t
        )
      );
      setEditingTerm(null);
      setNewContent("");
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText("support@adda-farm.com");
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2200);
  };

  const copyPageLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2200);
  };

  const handlePrint = () => {
    window.print();
  };

  const lastUpdated = terms[0]?.updated_at
    ? new Date(terms[0].updated_at).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "August 2026";

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans pb-16">
      {/* ── Top Header Navigation Bar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs sm:text-sm font-semibold transition-all shadow-sm active:scale-95"
            >
              <FaArrowLeft className="text-xs" />
              <span>Dashboard</span>
            </button>
            <div className="h-5 w-[1px] bg-slate-300 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight">
                Adda Farm <span className="text-blue-600">Legal Portal</span>
              </span>
            </div>
          </div>

          {/* Navigation Segmented Switch */}
          <div className="hidden sm:flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
            <button
              onClick={() => navigate("/privacy")}
              className="px-3.5 py-1.5 rounded-lg bg-white text-blue-600 shadow-sm flex items-center gap-1.5 transition-all"
            >
              <FaShieldAlt className="text-xs" />
              <span>Privacy Policy</span>
            </button>
            <button
              onClick={() => navigate("/terms")}
              className="px-3.5 py-1.5 rounded-lg text-slate-600 hover:text-slate-900 flex items-center gap-1.5 transition-all"
            >
              <FaFileContract className="text-xs" />
              <span>Terms & Conditions</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={copyPageLink}
              title="Copy link"
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg text-sm transition-colors"
            >
              {copiedLink ? <FaCheckCircle className="text-emerald-500" /> : <FaRegCopy />}
            </button>
            <button
              onClick={handlePrint}
              title="Print Policy"
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg text-sm transition-colors hidden sm:block"
            >
              <FaPrint />
            </button>
            {terms[0] && (
              <button
                onClick={() => startEdit(terms[0])}
                className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-xs sm:text-sm font-bold shadow-sm hover:shadow transition-all active:scale-95"
              >
                <FaEdit className="text-xs" />
                <span>Edit Policy</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero Brand Header ────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white pt-12 pb-16 px-4 sm:px-6 lg:px-8 border-b border-indigo-900">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center gap-2.5 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-400/30">
              <FaShieldAlt className="text-xs text-blue-400" />
              Official Compliance Framework
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
              <FaCheckCircle className="text-xs text-emerald-400" />
              Verified & In Effect
            </span>
            <span className="text-xs text-indigo-300/80 font-medium ml-auto">
              Last Revised: {lastUpdated}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-4">
            Privacy Policy & Field Operating Terms
          </h1>
          <p className="text-base sm:text-lg text-indigo-100/90 max-w-3xl leading-relaxed font-normal">
            This document outlines how Adda Farm collects, cryptographically secures, and processes
            identity, location, and banking data for Field Promoters, Agricultural Workers, and Users.
          </p>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 mt-8 pt-8 border-t border-indigo-900/60">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3.5">
              <div className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-0.5">
                Encryption
              </div>
              <div className="text-lg font-bold text-white">AES-256 / TLS</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3.5">
              <div className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-0.5">
                Data Selling
              </div>
              <div className="text-lg font-bold text-emerald-400">Strictly Zero</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3.5">
              <div className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-0.5">
                Payout Gate
              </div>
              <div className="text-lg font-bold text-white">KYC Verified</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3.5">
              <div className="text-indigo-300 text-xs font-semibold uppercase tracking-wider mb-0.5">
                Support Desk
              </div>
              <div className="text-lg font-bold text-white">24h Response</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Main Container & Filter Bar ──────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        {/* Controls Card */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg border border-slate-200/80 mb-8 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          {/* Search Box */}
          <div className="relative flex-1">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search policy sections, keywords (e.g. payout, encryption, Aadhaar)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
            {[
              { id: "all", label: "All Clauses" },
              { id: "data", label: "Data & KYC" },
              { id: "security", label: "Security" },
              { id: "compliance", label: "Conduct" },
              { id: "payouts", label: "Payouts" },
              { id: "rights", label: "Rights" },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Expand / Collapse All */}
          <div className="flex items-center gap-2 border-t md:border-t-0 pt-2 md:pt-0 border-slate-100 self-end md:self-auto">
            <button
              onClick={expandAll}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 px-2 py-1"
            >
              Expand All
            </button>
            <span className="text-slate-300">•</span>
            <button
              onClick={collapseAll}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-2 py-1"
            >
              Collapse
            </button>
          </div>
        </div>

        {/* ── Policy Preamble Notice ──────────────────────────────────────── */}
        {parsedPolicyData.preamble && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 flex items-start gap-3.5 shadow-sm">
            <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 text-sm shadow-sm mt-0.5">
              <FaInfoCircle />
            </div>
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-blue-800 bg-blue-100 px-2 py-0.5 rounded-md inline-block mb-1">
                Legal Acknowledgment
              </span>
              <p className="text-sm font-semibold text-slate-800 leading-relaxed">
                {parsedPolicyData.preamble}
              </p>
            </div>
          </div>
        )}

        {/* ── Policy Sections List ─────────────────────────────────────────── */}
        {loading ? (
          <div className="bg-white rounded-2xl p-16 border border-slate-200 text-center shadow-sm">
            <div className="inline-block w-9 h-9 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-slate-600 font-semibold">Loading official policy clauses...</p>
          </div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 p-6 rounded-2xl mb-6 text-center">
            <FaExclamationTriangle className="inline-block text-xl mr-2 mb-1" />
            <span className="font-semibold">{error}</span>
          </div>
        ) : filteredSections.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-200 text-center">
            <FaInfoCircle className="text-3xl text-slate-400 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-800 mb-1">No matching clauses found</h3>
            <p className="text-xs text-slate-500">
              No policy sections matched "{searchQuery}". Try a different keyword or reset filters.
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setActiveCategory("all");
              }}
              className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSections.map((sec) => {
              const Icon = sec.icon || FaFileContract;
              const isExpanded = expandedSections[sec.id] !== false; // Default expanded for readability

              return (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all overflow-hidden"
                >
                  {/* Section Accordion Header */}
                  <div
                    onClick={() => toggleSection(sec.id)}
                    className="p-5 sm:p-6 flex items-start sm:items-center justify-between gap-4 cursor-pointer select-none bg-white hover:bg-slate-50/70 transition-colors"
                  >
                    <div className="flex items-start sm:items-center gap-3.5">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center text-lg flex-shrink-0">
                        <Icon />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-extrabold bg-slate-100 text-slate-700">
                            Clause {sec.num}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-blue-50 text-blue-700">
                            {sec.tag}
                          </span>
                        </div>
                        <h2 className="text-base sm:text-lg font-bold text-slate-900">
                          {sec.title}
                        </h2>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-slate-400">
                      <span className="text-xs font-semibold hidden md:block">
                        {isExpanded ? "Collapse" : "Read More"}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                        {isExpanded ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                      </div>
                    </div>
                  </div>

                  {/* Section Expanded Body */}
                  {isExpanded && (
                    <div className="px-5 sm:px-6 pb-6 pt-3 border-t border-slate-100 space-y-4">
                      {sec.subBlocks && sec.subBlocks.length > 0 ? (
                        sec.subBlocks.map((sub, sIdx) => {
                          if (sub.subTitle) {
                            return (
                              <div
                                key={sIdx}
                                className="bg-slate-50/90 border border-slate-200/90 rounded-xl p-4 sm:p-5 shadow-xs"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-black">
                                    {sub.subNum}
                                  </span>
                                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                                    {sub.subTitle}
                                  </h3>
                                </div>
                                {sub.intro && (
                                  <p className="text-xs sm:text-sm text-slate-600 mb-3 italic">
                                    {sub.intro}
                                  </p>
                                )}
                                {sub.bullets && sub.bullets.length > 0 && (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                    {sub.bullets.map((b, bIdx) => (
                                      <div
                                        key={bIdx}
                                        className="flex items-start gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200/70 shadow-xs"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></span>
                                        <span className="text-xs sm:text-sm text-slate-800 font-medium leading-relaxed">
                                          {b}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          // General block without sub-heading
                          return (
                            <div key={sIdx} className="space-y-3">
                              {sub.intro && (
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line font-medium">
                                  {sub.intro}
                                </p>
                              )}
                              {sub.bullets && sub.bullets.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {sub.bullets.map((b, bIdx) => (
                                    <div
                                      key={bIdx}
                                      className="flex items-start gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200/70"
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></span>
                                      <span className="text-xs sm:text-sm text-slate-800 font-medium leading-relaxed">
                                        {b}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-line font-normal text-slate-700">
                          {sec.rawContent}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Official Compliance & Grievance Contact Card ──────────────────── */}
        <div className="mt-10 bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 text-white rounded-3xl p-6 sm:p-10 shadow-xl border border-indigo-800">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="space-y-3 max-w-2xl">
              <div className="flex items-center gap-2 text-blue-300 text-xs font-extrabold uppercase tracking-widest">
                <FaBuilding className="text-sm" />
                <span>Adda Farm Legal & Compliance Desk</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                Grievance Officer & Data Redressal
              </h3>
              <p className="text-sm text-indigo-100/80 leading-relaxed">
                If you have questions regarding identity document verification, incentive eligibility,
                privacy rights, or wish to exercise your statutory rights under DPDPA, our compliance desk
                is ready to assist.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2 text-xs text-indigo-200 font-medium">
                <div className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-emerald-400" />
                  <span>24–48h Turnaround</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-emerald-400" />
                  <span>Statutory Compliance</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FaCheckCircle className="text-emerald-400" />
                  <span>Encrypted Channel</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-72 flex-shrink-0">
              <button
                onClick={copyEmail}
                className="flex items-center justify-center gap-2.5 px-5 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-2xl text-sm transition-all shadow-sm active:scale-98"
              >
                {copiedEmail ? <FaCheckCircle className="text-emerald-400" /> : <FaRegCopy />}
                <span>{copiedEmail ? "Copied to Clipboard!" : "support@adda-farm.com"}</span>
              </button>

              <a
                href="mailto:support@adda-farm.com?subject=Adda%20Farm%20Privacy%20Policy%20Inquiry"
                className="flex items-center justify-center gap-2 px-5 py-3.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-extrabold rounded-2xl text-sm shadow-lg shadow-blue-500/25 transition-all active:scale-98"
              >
                <FaEnvelope />
                <span>Email Grievance Desk</span>
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* ── Admin Edit Policy Modal ──────────────────────────────────────────── */}
      {editingTerm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-3xl shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center text-lg">
                  <FaEdit />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-black text-slate-900">
                    Edit Privacy Policy & Operating Terms
                  </h2>
                  <p className="text-xs text-slate-400">
                    Live updates to terms across the fieldworker portal & mobile app
                  </p>
                </div>
              </div>

              <span
                className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${
                  unlocked
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                    : "bg-amber-100 text-amber-800 border border-amber-200"
                }`}
              >
                {unlocked ? <FaCheckCircle /> : <FaKey />}
                <span>{unlocked ? "PIN Verified — Unlocked" : "Locked (Requires PIN)"}</span>
              </span>
            </div>

            <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
              <span className="font-bold uppercase tracking-wider text-slate-600">
                Policy Document Text (Numbered Clauses)
              </span>
              <span>{newContent.length} characters</span>
            </div>

            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              className="w-full border border-slate-300 rounded-2xl p-4 mb-4 text-xs sm:text-sm text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all leading-relaxed"
              rows={14}
              placeholder="1. CLAUSE TITLE&#10;Clause content..."
            />

            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-2">
              {!unlocked ? (
                <button
                  onClick={() => setShowKeypad(true)}
                  className="w-full sm:w-auto px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <FaLock />
                  <span>Enter PIN to Unlock</span>
                </button>
              ) : (
                <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1.5">
                  <FaCheckCircle /> Ready to commit changes
                </span>
              )}

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <button
                  onClick={() => setEditingTerm(null)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!unlocked || saving}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Changes</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Keypad Security Modal ──────────────────────────────────────────── */}
      {showKeypad && (
        <KeypadModal
          type="secondary"
          onGoClick={unlockEdit}
          onClose={() => setShowKeypad(false)}
        />
      )}
    </div>
  );
}


